import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY } from "@/lib/api";

const ReloginDialog = lazy(() => import("@/components/site/ReloginDialog").then((m) => ({ default: m.ReloginDialog })));

const SiteContext = createContext(null);
export const useSite = () => useContext(SiteContext);

export class ReloginNeeded extends Error {}

const CACHE_KEY = "of_settings_cache";
const CONTENT_KEY = "of_content_cache";
const IMAGES_KEY = "of_images_cache";
const cached = (key = CACHE_KEY) => {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
};

export function SiteProvider({ children }) {
  const [settings, setSettings] = useState(cached);
  const [content, setContent] = useState(() => cached(CONTENT_KEY) || {});
  const [images, setImages] = useState(() => window.__ofImages || cached(IMAGES_KEY) || {});
  const [admin, setAdmin] = useState(null);
  const [reloginOpen, setReloginOpen] = useState(false);

  const refresh = useCallback(async () => {
    const early = window.__ofImgP;
    window.__ofImgP = null;
    (early ? early.then((d) => d || Promise.reject()) : api.get("/site-images").then((r) => r.data)).catch(() => api.get("/site-images").then((r) => r.data))
      .then((data) => {
        setImages(data);
        localStorage.setItem(IMAGES_KEY, JSON.stringify(data));
      }).catch(() => {});
    api.get("/content").then(({ data }) => {
      setContent(data);
      localStorage.setItem(CONTENT_KEY, JSON.stringify(data));
    }).catch(() => {});
    try {
      const { data } = await api.get("/settings/public");
      setSettings(data);
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch {
      setSettings((s) => s || {});
    }
  }, []);

  useEffect(() => {
    refresh();
    if (!localStorage.getItem(TOKEN_KEY)) return;
    api
      .get("/auth/me")
      .then(({ data }) => setAdmin(data))
      .catch(() => localStorage.removeItem(TOKEN_KEY));
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setAdmin({ email: data.email });
    return data;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAdmin(null);
  };

  const adminCall = async (fn) => {
    try {
      return await fn();
    } catch (err) {
      if (err?.response?.status === 401) {
        setReloginOpen(true);
        throw new ReloginNeeded("Please log in again.");
      }
      throw err;
    }
  };

  const saveSettings = async (patch) => {
    const { data } = await adminCall(() => api.patch("/admin/settings", patch));
    await refresh();
    return data;
  };

  const saveContent = async (key, value) => {
    const { data } = await adminCall(() => api.put(`/admin/content/${key}`, { value }));
    setContent((c) => {
      const next = { ...c, [key]: data.value };
      localStorage.setItem(CONTENT_KEY, JSON.stringify(next));
      return next;
    });
    return data.value;
  };

  const setImage = (slot, view) => setImages((m) => {
    const next = { ...m };
    if (view) next[slot] = view; else delete next[slot];
    localStorage.setItem(IMAGES_KEY, JSON.stringify(next));
    return next;
  });

  return (
    <SiteContext.Provider value={{ settings, content, images, setImage, refresh, admin, isAdmin: !!admin, login, logout, saveSettings, saveContent, adminCall }}>
      {children}
      {reloginOpen && (
        <Suspense fallback={null}>
          <ReloginDialog open={reloginOpen} onClose={() => setReloginOpen(false)} login={login} />
        </Suspense>
      )}
    </SiteContext.Provider>
  );
}
